import { PromiseCompletionSource } from "scene-manager";
import { ICanvasScaler } from "../../../../../canvas/ICanvasScaler";
import { IDestroyOptions, TextStyle } from "pixi.js";
import { SettingsController } from "../../../../common/controllers/SettingsController";
import { SettingsResourcesPackage } from "../resources/SettingsResourcesPackage";
import { CommonResourcesPackage } from "../../../../common/resources/CommonResourcesPackage";
import { BaseSettingsView } from "../../../../common/views/BaseSettingsView";
import { SettingsModel } from "../../../../common/models/SettingsModel";
import { UIButtonBase } from "../../../../common/components/UIButtonBase";
import { AdaptiveText } from "ui-pixijs";
import { GameFont } from "../../../../common/GameFont";
import { ResetComponent } from "../components/ResetComponent";
import { LocalizationModel } from "../../../../common/models/LocalizationModel";
import { LocalizationKey } from "../../../../localization/Localization";
import { nameof } from "bindable-data";

export class SettingsView extends BaseSettingsView<void> {
    protected resetButton: UIButtonBase;
    protected resetButtonText: AdaptiveText;
    protected resetComponent: ResetComponent;
    public constructor(
        settingsController: SettingsController,
        commonResources: CommonResourcesPackage,
        private readonly settingsResources: SettingsResourcesPackage,
        settingsModel: SettingsModel,
        private readonly localization: LocalizationModel,
        canvasScaler: ICanvasScaler,
        operationControls: PromiseCompletionSource<void>
    ) {
        super(
            commonResources,
            settingsModel,
            settingsController,
            settingsResources.assets_popups_settings_frame_big_png,
            canvasScaler,
            operationControls
        );

        this.closeButton.onUp.add(this.onCloseButtonClick, this);
        this.resetButton = new UIButtonBase({ defaultTexture: settingsResources.assets_popups_settings_reset_button_png });
        this.resetButton.position.set(187, 640);
        this.resetButtonText = new AdaptiveText(localization.getText(LocalizationKey.ResetProgress), new ResetButtonTextStyle(), 300, 140);
        this.resetButton.defaultView.addChild(this.resetButtonText);
        this.frame.addChild(this.resetButton);
        this.resetButtonText.updateText(false);
        this.resetButtonText.position.set((this.resetButton.width - this.resetButtonText.width) * 0.5, (this.resetButton.height - this.resetButtonText.height) * 0.5 - 13);
        this.resetButton.onUp.add(this.onResetButtonClick, this);
        this.settingsModel.propertyChanged.add(this.onSettingsModelPropertyChange, this);
    }

    public override destroy(options?: IDestroyOptions | boolean): void {
        this.closeButton.onUp.remove(this.onCloseButtonClick, this);
        this.resetButton.onUp.remove(this.onResetButtonClick, this);
        this.settingsModel.propertyChanged.remove(this.onSettingsModelPropertyChange, this);
        super.destroy(options);
    }

    public start(): void {
    }

    protected layout(width: number, height: number): void {
        switch (height) {
            case 2400:
                this.layout9x20(width);
                break;
            case 1620:
                this.layout10x9();
                break;
            case 1800:
                this.layout4x3();
                break;
        }
        this.closeButton.position.set(581, -64);
        this.musicToggle.position.set(125, 74);
        this.soundToggle.position.set(404, 74);
        this.languagesBox.position.set(79, 275);
        this.resetButton?.position.set(187, 640);
    }

    private layout9x20(width: number): void {
        this.frame.position.set((width - this.frame.width) * 0.5, (2400 - this.frame.width) * 0.5);
        this.resetComponent?.position.set((width - this.frame.width) * 0.5, (2400 - this.frame.width) * 0.5);
    }

    private layout10x9(): void {
        this.frame.position.set(1061, 459);
        this.resetComponent?.position.set(1061, 459);
    }

    private layout4x3(): void {
        this.frame.position.set(821, 536);
        this.resetComponent?.position.set(821, 536);
    }

    private onResetButtonClick(): void {
        this.frame.visible = false;
        this.resetComponent = new ResetComponent(this.commonResources, this.settingsResources, this.localization);
        this.layout(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.addChild(this.resetComponent);
        this.resetComponent.onNoButtonUp.add(this.onNoResetButtonClick, this);
        this.resetComponent.onYesButtonClick.add(this.onYesResetButtonClick, this);
        this.resetComponent.onCloseButtonUp.add(this.onCloseButtonClick, this);
    }

    private onYesResetButtonClick(): void {
        this.settingsController.resetGameProgress();
    }

    private onNoResetButtonClick(): void {
        this.resetComponent.onNoButtonUp.remove(this.onNoResetButtonClick, this);
        this.resetComponent.destroy();
        this.resetComponent = null;
        this.frame.visible = true;
    }

    private onCloseButtonClick(): void {
        this.operationControls.complete();
    }

    private onSettingsModelPropertyChange(model: SettingsModel, key: string): void {
        if (key != nameof(model, "language")) {
            return;
        }
        this.resetButtonText.text = this.localization.getText(LocalizationKey.ResetProgress);
        this.resetButtonText.position.set((this.resetButton.width - this.resetButtonText.width) * 0.5, (this.resetButton.height - this.resetButtonText.height) * 0.5 - 13);
    }
}

class ResetButtonTextStyle extends TextStyle {
    public constructor() {
        super({
            fontSize: 48,
            align: "center",
            fill: "#FFFFFF",
            fontFamily: GameFont.Default
        })
    }
}
